import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  createEvalReplayMutation,
  getEvalExperimentOptions,
  listEvalExperimentsOptions,
  listEvalReplaysOptions,
  listEvalRunLinesOptions,
} from '@/api/generated/@tanstack/react-query.gen';
import type { EvalRunLine } from '@/api/generated/types.gen';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { StatusPill } from '@/components/Evals/format';
import { cn } from '@/lib/utils';
import { ProviderBadge } from '@/components/Evals/ProviderBadge';

export const Route = createFileRoute('/_auth/evals/replays/')({
  component: ReplaysPage,
});

const MODELS = ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5', 'gemini-3.8-flash', 'gemini-3.5-flash', 'gpt-6-sol', 'gpt-5.4'];

function ReplaysPage() {
  const replays = useQuery({
    ...listEvalReplaysOptions(),
    refetchInterval: (q) => (q.state.data?.some((r) => ['queued', 'running'].includes(r.status)) ? 4000 : false),
  });
  return (
    <EvalShell
      title="Replay lab"
      subtitle="Regenerate lines with another model or prompt, judged side by side."
    >
      <ReplayBuilder />
      <Panel title="Replays">
        {!replays.data ? (
          <Skeleton className="h-32 rounded-2xl" />
        ) : !replays.data.length ? (
          <p className="text-sm text-neutral-400">No replays yet.</p>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-neutral-400">
                <th className="py-1.5 pr-3 font-medium">Replay</th>
                <th className="py-1.5 pr-3 font-medium">Variant</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 pr-3 font-medium">Good lines</th>
                <th className="py-1.5 font-medium">Fixed / broke</th>
              </tr>
            </thead>
            <tbody>
              {replays.data.map((r) => (
                <tr key={r.id} className="border-t border-neutral-100">
                  <td className="py-2 pr-3">
                    <Link to="/evals/replays/$replayId" params={{ replayId: r.id }} className="font-medium text-neutral-900 hover:underline">
                      {r.name}
                    </Link>
                  </td>
                  <td className="py-2 pr-3 text-neutral-600">
                    <ProviderBadge id={r.variant.model} />
                    {(r.variant.instructions != null || r.variant.extra_instruction) && (
                      <span className="text-neutral-400"> · instructions edited</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="py-2 pr-3 font-mono text-[12px]">
                    {r.summary?.original_good ?? 0} → {r.summary?.new_good ?? 0} / {r.summary?.lines ?? 0}
                  </td>
                  <td className="py-2 font-mono text-[12px]">
                    <span className="text-emerald-600">+{r.summary?.fixed ?? 0}</span>{' '}
                    <span className="text-rose-600">−{r.summary?.broke ?? 0}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </EvalShell>
  );
}

function ReplayBuilder() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const experiments = useQuery(listEvalExperimentsOptions());
  const [experimentId, setExperimentId] = useState('');
  const experiment = useQuery({ ...getEvalExperimentOptions({ path: { experiment_id: experimentId } }), enabled: !!experimentId });
  const runs = (experiment.data?.runs ?? []).filter((r) => r.status === 'done' || r.status === 'invalid');
  const lineQueries = useQueries({
    queries: runs.map((r) => ({ ...listEvalRunLinesOptions({ path: { run_id: r.id } }) })),
  });
  const lines: (EvalRunLine & { label: string })[] = useMemo(
    () =>
      lineQueries.flatMap((q, i) =>
        (q.data ?? []).map((line) => ({ ...line, label: `${runs[i]?.fixture_key} · ${runs[i]?.persona}` })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineQueries.map((q) => q.dataUpdatedAt).join(','), runs.length],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [model, setModel] = useState('claude-opus-5-5');
  const [extra, setExtra] = useState('');
  const [instructions, setInstructions] = useState('');
  const [replaceInstructions, setReplaceInstructions] = useState(false);
  const [name, setName] = useState('');
  const key = (l: EvalRunLine) => `${l.run_id}:${l.target_id}`;
  const pick = (filter: (l: EvalRunLine) => boolean) => setSelected(new Set(lines.filter(filter).map(key)));

  const mutation = useMutation({
    ...createEvalReplayMutation(),
    onSuccess: (r) => {
      toast.success('Replay queued');
      queryClient.invalidateQueries();
      navigate({ to: '/evals/replays/$replayId', params: { replayId: r.id } });
    },
    onError: () => toast.error("Couldn't queue it"),
  });

  return (
    <Panel title="New replay">
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          <Select value={experimentId} onValueChange={(v) => (setExperimentId(v), setSelected(new Set()))}>
            <SelectTrigger className="h-9 rounded-full text-sm">
              <SelectValue placeholder="Take lines from an experiment…" />
            </SelectTrigger>
            <SelectContent>
              {(experiments.data ?? []).map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {experimentId && (
            <>
              <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
                <span className="text-neutral-500">Select:</span>
                {[
                  { label: 'You marked wrong', f: (l: EvalRunLine) => l.human_label === 'bad' },
                  { label: 'Judge marked bad', f: (l: EvalRunLine) => l.judge_label === 'bad' },
                  { label: 'Judge marked good', f: (l: EvalRunLine) => l.judge_label === 'good' },
                  { label: 'All', f: () => true },
                  { label: 'None', f: () => false },
                ].map(({ label, f }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => pick(f)}
                    className="h-7 rounded-full bg-neutral-100 px-2.5 font-medium text-neutral-600 hover:bg-neutral-200"
                  >
                    {label}
                  </button>
                ))}
                <span className="ml-auto text-neutral-500">
                  {selected.size} of {lines.length} lines
                </span>
              </div>
              <div className="max-h-96 divide-y divide-neutral-100 overflow-y-auto rounded-2xl ring-1 ring-black/5">
                {!lines.length && <p className="p-3 text-sm text-neutral-400">No judged lines in this experiment yet.</p>}
                {lines.map((l) => (
                  <label key={key(l)} className="flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-neutral-50">
                    <Checkbox
                      className="mt-0.5"
                      checked={selected.has(key(l))}
                      onCheckedChange={(on) =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (on === true) next.add(key(l));
                          else next.delete(key(l));
                          return next;
                        })
                      }
                    />
                    <span className="flex-1 text-[13px] text-neutral-800">{l.text}</span>
                    <span className="flex shrink-0 flex-col items-end gap-0.5 text-[10px]">
                      <span className="text-neutral-400">{l.label}</span>
                      <span className={cn(l.judge_label === 'bad' ? 'text-rose-600' : 'text-emerald-600')}>judge {l.judge_label}</span>
                      {l.human_label && (
                        <span className={cn(l.human_label === 'bad' ? 'text-rose-600' : 'text-emerald-600')}>you {l.human_label}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="space-y-3">
          <Input placeholder="Name, e.g. Sonnet + no narration rule" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          <label className="block space-y-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">Model</span>
            <Input list="replay-models" value={model} onChange={(e) => setModel(e.target.value)} className="h-9 text-sm" />
            <datalist id="replay-models">
              {MODELS.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">Add to the instructions</span>
            <Textarea
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="e.g. Never describe what you are about to do; just do it."
              className="min-h-20 text-[12px]"
            />
          </label>
          <label className="flex items-center gap-2 text-[12px] text-neutral-600">
            <Checkbox checked={replaceInstructions} onCheckedChange={(v) => setReplaceInstructions(v === true)} />
            Replace the base instructions entirely
          </label>
          {replaceInstructions && (
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="The full base instructions the model gets instead of what the run used"
              className="min-h-40 font-mono text-[11px]"
            />
          )}
          <p className="text-[11px] leading-relaxed text-neutral-500">
            Each line is regenerated from what the model had then: the base instructions, the step directive, and the
            conversation so far. Voice sessions are replayed as text, so this measures what is said, not how.
          </p>
          <Button
            className="btn-3d h-9 w-full rounded-full"
            disabled={!selected.size || !name || !model || mutation.isPending}
            onClick={() =>
              mutation.mutate({
                body: {
                  name,
                  variant: {
                    model,
                    extra_instruction: extra,
                    instructions: replaceInstructions ? instructions : null,
                  },
                  lines: [...selected].map((k) => {
                    const [run_id, target_id] = k.split(/:(.+)/);
                    return { run_id, target_id };
                  }),
                },
              })
            }
          >
            Replay {selected.size} line{selected.size === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
